import React, { useState } from 'react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { KpiCard } from '../components/KpiCard'
import { ChartCard } from '../components/ChartCard'
import { AnalyticsTable, RateCellRenderer, RedValueRenderer } from '../components/AnalyticsTable'
import { SubTabBar } from '../components/SubTabBar'
import { KpiRow, InfoBanner } from '../HrAnalytics.styled'
import {
  MOCK_SCHEDULE_KPIS,
  MOCK_PLANNED_VS_ACTUAL,
  MOCK_COVERAGE_GAPS,
  MOCK_SCHEDULE_BY_DEPT,
  MOCK_SCHEDULE_BY_ROLE,
} from 'features/hrAnalytics/mockData/schedule.mock'

const KpiIcons: Record<string, React.ReactNode> = {
  calendar: <svg viewBox='0 0 24 24'><path d='M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM9 10H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z' /></svg>,
  checkCircle: <svg viewBox='0 0 24 24'><path d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z' /></svg>,
  warning: <svg viewBox='0 0 24 24'><path d='M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z' /></svg>,
}

const GAP_COLUMNS = [
  { key: 'date', label: 'Date', sortable: true, width: '15%' },
  { key: 'planned', label: 'Planned', sortable: true, width: '20%' },
  { key: 'actual', label: 'Actual', sortable: true, width: '20%' },
  {
    key: 'gap',
    label: 'Gap',
    sortable: true,
    width: '15%',
    renderCell: (val: number) => <RedValueRenderer value={val} />,
  },
  {
    key: 'coverage',
    label: 'Coverage',
    sortable: true,
    width: '30%',
    renderCell: (val: number) => <RateCellRenderer value={val} />,
  },
]

const DEPT_COLUMNS = [
  { key: 'name', label: 'Name', sortable: true, width: '20%' },
  { key: 'planned', label: 'Planned', sortable: true, width: '18%' },
  { key: 'actual', label: 'Actual', sortable: true, width: '18%' },
  {
    key: 'gap',
    label: 'Gap',
    sortable: true,
    width: '14%',
    renderCell: (val: number) => <RedValueRenderer value={val} />,
  },
  {
    key: 'coverage',
    label: 'Coverage',
    sortable: true,
    width: '30%',
    renderCell: (val: number) => <RateCellRenderer value={val} />,
  },
]

export const ScheduleTab = () => {
  const [period, setPeriod] = useState('Day')
  const [tableTab, setTableTab] = useState('department')

  const deptData = tableTab === 'department' ? MOCK_SCHEDULE_BY_DEPT : MOCK_SCHEDULE_BY_ROLE

  return (
    <>
      <KpiRow>
        {MOCK_SCHEDULE_KPIS.map((kpi, idx) => (
          <KpiCard
            key={idx}
            icon={KpiIcons[kpi.iconKey || '']}
            value={kpi.value}
            label={kpi.label}
            sublabel={kpi.sublabel}
            accentColor={kpi.accentColor}
          />
        ))}
      </KpiRow>

      <InfoBanner>
        Schedule analytics compares planned obligations to finalized outcomes. It does not alter the schedule or enforce compliance.
      </InfoBanner>

      <ChartCard
        title='Planned vs Actual'
        periods={['Day', 'Week', 'Month']}
        activePeriod={period}
        onPeriodChange={setPeriod}
        legend={[
          { label: 'Actual', color: '#00B04C' },
          { label: 'Planned', color: '#FF4F46' },
        ]}
      >
        <ResponsiveContainer width='100%' height={300}>
          <BarChart data={MOCK_PLANNED_VS_ACTUAL} barGap={2}>
            <CartesianGrid strokeDasharray='3 3' stroke='#46425520' />
            <XAxis dataKey='date' tick={{ fontSize: 11, fill: '#B0B0B0' }} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: '#B0B0B0' }} tickLine={false} />
            <Tooltip />
            <Bar dataKey='actual' fill='#00B04C' radius={[4, 4, 0, 0]} barSize={12} />
            <Bar dataKey='planned' fill='#FF4F46' radius={[4, 4, 0, 0]} barSize={12} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title='Largest Coverage Gaps'>
        <AnalyticsTable columns={GAP_COLUMNS} data={MOCK_COVERAGE_GAPS} />
      </ChartCard>

      <SubTabBar
        tabs={[
          { key: 'department', label: 'By Department' },
          { key: 'role', label: 'By Role' },
        ]}
        activeTab={tableTab}
        onTabChange={setTableTab}
      />

      <AnalyticsTable columns={DEPT_COLUMNS} data={deptData} />
    </>
  )
}
